<!-- TODO: consider moving to common package -->
<template>
  <div ref="referenceRef" class="v-tooltip-ui" :class="{ 'v-tooltip-ui--floating': floating }">
    <slot name="activator" :on="onEvents" :attrs="attrs" />
    <Transition name="tooltip">
      <div v-if="isShown" ref="floatingRef" class="v-tooltip-ui__cloud" :style="floatingStyles">
        <span v-if="floating" ref="arrowRef" class="v-tooltip-ui__arrow" :style="arrowStyles" />
        <div class="v-tooltip-ui__cloud-content">
          <slot />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
  import {
    useFloating,
    offset as floatingOffset,
    flip,
    shift,
    arrow,
    autoUpdate,
  } from '@floating-ui/vue';

  // Types
  import type { PropType, Ref, ComputedRef, StyleValue } from 'vue';
  import type { MiddlewareData, Placement, Side } from '@floating-ui/dom';
  import type { TAttrs, TOnEvents } from '~/misc/v-tooltip/types';

  // Config
  import { GAP_IN_PX, VIEWPORT_PADDING_IN_PX, ARROW_SIZE_IN_PX } from '~/misc/v-tooltip/config';

  const props = defineProps({
    floating: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String as PropType<Placement>,
      default: 'top',
    },
    offset: {
      type: Number,
      default: GAP_IN_PX,
    },
  });

  // The cloud edge the arrow sits on is opposite the cloud's resolved side.
  const OPPOSITE_SIDE: Record<Side, Side> = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  };

  const isShown: Ref<boolean> = ref<boolean>(false);
  const referenceRef: Ref<HTMLElement | null> = ref<HTMLElement | null>(null);
  const floatingRef: Ref<HTMLElement | null> = ref<HTMLElement | null>(null);
  const arrowRef: Ref<HTMLElement | null> = ref<HTMLElement | null>(null);

  // Only run Floating UI positioning when enabled. `fixed` strategy lets the
  // cloud break out of clipping ancestors (e.g. a card with `overflow: hidden`);
  // flip/shift keep it inside the viewport while the arrow tracks the activator.
  const floatingPosition: ReturnType<typeof useFloating> | null = props.floating
    ? useFloating(referenceRef, floatingRef, {
        placement: props.placement,
        strategy: 'fixed',
        middleware: [
          floatingOffset(props.offset),
          flip(),
          shift({ padding: VIEWPORT_PADDING_IN_PX }),
          arrow({ element: arrowRef }),
        ],
        whileElementsMounted: autoUpdate,
      })
    : null;

  const floatingStyles: ComputedRef<StyleValue> = computed<StyleValue>(
    () => floatingPosition?.floatingStyles.value ?? ''
  );

  const arrowStyles: ComputedRef<StyleValue> = computed<StyleValue>(() => {
    const arrowData: MiddlewareData['arrow'] = floatingPosition?.middlewareData.value.arrow;

    if (!floatingPosition || !arrowData) {
      return '';
    }

    const side: Side = floatingPosition.placement.value.split('-')[0] as Side;

    return {
      left: arrowData.x != null ? `${arrowData.x}px` : '',
      top: arrowData.y != null ? `${arrowData.y}px` : '',
      [OPPOSITE_SIDE[side]]: `${-ARROW_SIZE_IN_PX / 2}px`,
    };
  });

  const setIsShown = (value: boolean) => {
    isShown.value = value;
  };

  const show = () => setIsShown(true);

  const hide = () => setIsShown(false);

  const onEvents: TOnEvents = {
    mouseenter: show,
    mouseleave: hide,
    focusin: show,
    focusout: hide,
  };

  const attrs: ComputedRef<TAttrs> = computed<TAttrs>(() => ({
    ariaExpanded: isShown.value,
    ariaHasPopup: true,
    tabIndex: 0,
  }));
</script>
